// src/components/discovery/JiraEpicLinkDialog.jsx
// Fetches Jira epics and lets the user link one to a problem card
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Badge } from "components/ui/badge";
import { Loader2, Search, ExternalLink, Link2, Link2Off, CheckCircle2, AlertCircle } from "lucide-react";
import { fetchJiraInitiatives } from "../../adapters/jiraAdapter";
import { getIntegrationStatus } from "../../lib/integrations";
import { useAuth } from "../../contexts/AuthContext";

export default function JiraEpicLinkDialog({ open, onClose, problem, onLink }) {
  const { user } = useAuth();
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pre-select current linked epic if any
  useEffect(() => {
    if (open && problem?.jira_epic_key) {
      setSelected({ jira_key: problem.jira_epic_key, title: problem.jira_epic_title || problem.jira_epic_key });
    } else {
      setSelected(null);
    }
    setSearch("");
    setError(null);
  }, [open, problem]);

  const loadEpics = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const status = await getIntegrationStatus();
      if (!status.jira) {
        setError("Jira isn't connected. Connect your Jira on the Issues page first.");
        return;
      }

      const epicJql =
        "(issueType = Epic OR issueType = Feature OR issueType = Initiative) ORDER BY updated DESC";

      const fetched = await fetchJiraInitiatives({ jql: epicJql, max: 100 });

      setEpics(fetched);
    } catch (err) {
      setError(err.message || "Failed to fetch epics from JIRA.");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (open) {
      loadEpics();
    }
  }, [open, loadEpics]);

  const filtered = epics.filter((e) => {
    const q = search.toLowerCase();
    return (
      !q ||
      (e.title || "").toLowerCase().includes(q) ||
      (e.jira_key || "").toLowerCase().includes(q) ||
      (e.description || "").toLowerCase().includes(q)
    );
  });

  const handleLink = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onLink?.({
        jira_epic_key: selected.jira_key,
        jira_epic_title: selected.title,
        jira_epic_url: selected.external_url || null,
        jira_epic_status: selected.jira_status || null,
      });
      onClose();
    } catch (err) {
      setError("Failed to save link: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    setSaving(true);
    try {
      await onLink?.({
        jira_epic_key: null,
        jira_epic_title: null,
        jira_epic_url: null,
        jira_epic_status: null,
      });
      onClose();
    } catch (err) {
      setError("Failed to unlink: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const statusVariant = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("progress") || s.includes("development")) return "accent";
    if (s.includes("done") || s.includes("closed") || s.includes("resolved")) return "success";
    return "default";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: 580, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <DialogHeader>
          <DialogTitle style={{ fontSize: 17, fontWeight: 800 }}>
            Link Jira Epic
          </DialogTitle>
          <DialogDescription>
            Connect <strong>{problem?.title || "this problem"}</strong> to a Jira Epic or Feature.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 12, overflow: "hidden", flex: 1 }}>
          {/* Current link indicator */}
          {problem?.jira_epic_key && (
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              background: "var(--accent-soft)",
              border: "1px solid var(--accent-border)",
              borderRadius: 6,
              fontSize: 13,
            }}>
              <CheckCircle2 size={14} color="var(--accent)" />
              <span style={{ color: "var(--accent-fg)" }}>
                Currently linked to <strong>{problem.jira_epic_key}</strong>
                {problem.jira_epic_title ? ` — ${problem.jira_epic_title}` : ""}
              </span>
              {problem.jira_epic_url && (
                <a href={problem.jira_epic_url} target="_blank" rel="noreferrer" style={{ marginLeft: "auto", color: "var(--accent-fg)" }}>
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: 12,
              background: "var(--danger-soft)",
              border: "1px solid var(--danger-border)",
              borderRadius: 6,
              fontSize: 13,
              color: "var(--danger-fg)",
            }}>
              <AlertCircle size={14} style={{ marginTop: 2, flexShrink: 0 }} />
              {error}
            </div>
          )}

          {/* Search */}
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--fg-subtle)" }} />
            <Input
              placeholder="Search epics by key or title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
          </div>

          {/* Epic list */}
          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>
            {loading && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: 32, color: "var(--fg-muted)" }}>
                <Loader2 size={18} className="animate-spin" />
                Loading epics from Jira...
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--fg-muted)", fontSize: 14 }}>
                {epics.length === 0 ? "No epics found in Jira." : "No epics match your search."}
              </div>
            )}

            {!loading && filtered.map((epic) => {
              const isSelected = selected?.jira_key === epic.jira_key;
              return (
                <button
                  key={epic.jira_key}
                  onClick={() => setSelected(isSelected ? null : epic)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    padding: "10px 12px",
                    border: `1px solid ${isSelected ? "var(--accent)" : "var(--border)"}`,
                    borderRadius: 6,
                    background: isSelected ? "var(--accent-soft)" : "white",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.1s",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--fg-muted)",
                      fontFamily: "monospace",
                      background: "var(--bg-subtle)",
                      padding: "1px 6px",
                      borderRadius: 4,
                    }}>
                      {epic.jira_key}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--fg)", flex: 1 }}>
                      {epic.title}
                    </span>
                    {isSelected && <CheckCircle2 size={16} color="var(--accent)" />}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {epic.jira_status && (
                      <Badge variant={statusVariant(epic.jira_status)}>
                        {epic.jira_status}
                      </Badge>
                    )}
                    {epic.time_horizon && (
                      <Badge>
                        {epic.time_horizon}
                      </Badge>
                    )}
                    {epic.jira_type && (
                      <span style={{ fontSize: 11, color: "var(--fg-subtle)" }}>{epic.jira_type}</span>
                    )}
                  </div>
                  {epic.description && (
                    <p style={{ fontSize: 12, color: "var(--fg-muted)", margin: 0, lineHeight: 1.4 }}>
                      {epic.description.slice(0, 120)}{epic.description.length > 120 ? "…" : ""}
                    </p>
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <div style={{ display: "flex", gap: 8 }}>
              {problem?.jira_epic_key && (
                <Button
                  variant="outline"
                  onClick={handleUnlink}
                  disabled={saving}
                  style={{ color: "var(--danger)", borderColor: "var(--danger-border)" }}
                >
                  <Link2Off size={14} style={{ marginRight: 6 }} />
                  Unlink
                </Button>
              )}
              <Button variant="outline" onClick={() => { setEpics([]); loadEpics(); }} disabled={loading}>
                Refresh
              </Button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleLink}
                disabled={!selected || saving}
                style={{ background: "var(--accent)", color: "white", border: "none" }}
              >
                {saving ? (
                  <><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />Saving...</>
                ) : (
                  <><Link2 size={14} style={{ marginRight: 6 }} />Link Epic</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
